import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, Loader2, Music, Voicemail } from 'lucide-react';

/**
 * Dedicated voicemail-drop section shown during ringing / connected calls.
 * Plays the chosen pre-recorded message to the lead, then the call ends so the
 * agent can move on.
 * @param {Object} props
 * @param {Array<{id: string, name: string}>} props.drops - Agent's saved drops.
 * @param {'ringing'|'in-progress'|string} props.callState
 * @param {(vm: {id: string, name: string}) => Promise<void>} props.onDrop
 * @returns {JSX.Element}
 */
export default function VoicemailDropSection({ drops, callState, onDrop }) {
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!selected && drops.length) setSelected(drops[0].id);
  }, [drops, selected]);

  const connected = callState === 'in-progress';
  const vm = drops.find((d) => d.id === selected);

  async function drop() {
    if (!vm || busy) return;
    setBusy(true);
    try { await onDrop(vm); } finally { setBusy(false); }
  }

  return (
    <div className="rounded-xl border border-civix-border bg-civix-panelLight/60" data-testid="voicemail-drop-section">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-white/80"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2"><Voicemail className="w-4 h-4 text-civix-purpleSoft" /> Voicemail drop</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {drops.length === 0 ? (
            <p className="text-xs text-white/50">
              You have no voicemail drops yet. Record one in Settings and it will show up here.
            </p>
          ) : (
            <>
              <div className="relative">
                <Music className="w-3.5 h-3.5 text-civix-purple absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  className="w-full appearance-none bg-civix-panel border border-civix-border rounded-lg pl-8 pr-8 py-2.5 text-sm text-white focus:outline-hidden focus:border-civix-purple"
                  aria-label="Voicemail drop"
                >
                  {drops.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-white/40 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>

              <p className="text-xs text-white/55 text-center">
                Leave a pre-recorded message for this lead without manually repeating it.
                {!connected && <span className="block text-amber-300/80 mt-1">Available once the lead answers.</span>}
              </p>

              <button
                onClick={drop}
                disabled={!connected || busy || !vm}
                className="w-full py-3 rounded-lg bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm flex items-center justify-center gap-2 transition"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Voicemail className="w-4 h-4" />}
                {busy ? 'Dropping…' : 'Drop Voicemail'}
              </button>
            </>
          )}

          <Link
            to="/settings?tab=voicemail"
            className="flex items-center justify-center gap-1.5 text-xs text-civix-purpleSoft hover:text-white underline-offset-2 hover:underline"
          >
            Manage or record new voicemails in Settings → VoiceMail drop <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
